import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';

/**
 * UserApiService (Angular 18) — HTTP wrappers for the /v1/api/users domain.
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/api/userApi/userApi.service.js
 *
 * Exposed as `apiService.user.*` through the ApiService facade
 * (downgraded under the legacy name for the 396 b2b-ui consumers).
 *
 * Notes:
 *  - `update()` uses /v2 (matches the legacy ApiVersionFactory call).
 *  - `ssoCallback()` is a new method on this sub-service. The legacy
 *    AuthenticationService called `$http.get('/v2/api/sso/callback')` directly;
 *    grouping it here keeps user-related auth flow endpoints in one place.
 */

const USERS_BASE = '/v1/api/users';
const RBAC_USERS_BASE = '/v1/api/rbac/users';
const RBAC_PERMISSIONS_BASE = '/v1/api/rbac/permissions/users';

interface RolesNamespace {
  list(id: number | string): Promise<AjsResponse<unknown>>;
}

interface GroupsNamespace {
  list(id: number | string): Promise<AjsResponse<unknown>>;
  remove(id: number | string, type: string): Promise<AjsResponse<unknown>>;
}

interface PermissionsNamespace {
  list(userId: number | string): Promise<AjsResponse<unknown>>;
}

export interface LoginProps {
  email: string;
  password: string;
  /** Optional LDAP header. */
  header?: { name: string; value: string };
}

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private http = inject(HttpClient);

  // ============= Top-level methods =============

  login(props: LoginProps): Promise<AjsResponse<unknown>> {
    const headers: Record<string, string> = {};
    if (props.header?.name && props.header?.value) {
      headers[props.header.name] = props.header.value;
    }
    return wrapAjs(
      this.http.put<unknown>(
        USERS_BASE,
        { email: props.email, password: props.password },
        Object.keys(headers).length > 0 ? { headers } : undefined,
      ),
    );
  }

  logout(): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${USERS_BASE}/logout`));
  }

  confirm(props: { token: string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${USERS_BASE}/confirm`, {
        headers: { 'x-acme-token': props.token },
      }),
    );
  }

  recover(email: string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${USERS_BASE}/recoverPassword`, {
        headers: { 'x-acme-email': email },
      }),
    );
  }

  adminReset(props: { id: number | string; password: string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${USERS_BASE}/${props.id}/password`, { password: props.password }),
    );
  }

  changePassword(props: { password: string; token: string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.put<unknown>(`${USERS_BASE}/finishRecoverPassword`, props));
  }

  resetExpiredPassword(props: {
    email: string;
    oldPassword: string;
    password: string;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.put<unknown>(`${USERS_BASE}/resetPassword`, props));
  }

  create(props: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(USERS_BASE, props));
  }

  view(userId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${USERS_BASE}/${userId}`));
  }

  update(props: { id: number | string; [key: string]: unknown }): Promise<AjsResponse<unknown>> {
    // Legacy uses ApiVersionFactory to switch v1→v2 here.
    return wrapAjs(this.http.put<unknown>(`/v2/api/users/${props.id}`, props));
  }

  remove(userId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${USERS_BASE}/${userId}`));
  }

  /**
   * SSO callback exchange.
   *
   * Not present in the legacy userApi.service.js — the legacy
   * AuthenticationService called `$http.get('/v2/api/sso/callback')` directly.
   * Centralised here so the AuthenticationService can delegate to the API layer
   * instead of issuing raw HTTP calls.
   */
  ssoCallback(params: { RelayState: string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>('/v2/api/sso/callback', {
        params: { RelayState: params.RelayState },
      }),
    );
  }

  // ============= Sub-namespaces =============

  readonly roles: RolesNamespace = {
    list: (id) => wrapAjs(this.http.get<unknown>(`${RBAC_USERS_BASE}/${id}/roles`)),
  };

  readonly groups: GroupsNamespace = {
    list: (id) => wrapAjs(this.http.get<unknown>(`${RBAC_USERS_BASE}/${id}/groups`)),
    remove: (id, type) => wrapAjs(this.http.delete<unknown>(`${RBAC_USERS_BASE}/${id}/${type}`)),
  };

  readonly permissions: PermissionsNamespace = {
    list: (userId) => wrapAjs(this.http.get<unknown>(`${RBAC_PERMISSIONS_BASE}/${userId}`)),
  };
}
